import { Component, OnInit, Input, OnChanges, SimpleChanges, OnDestroy, HostListener, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { TabViewModule } from 'primeng/tabview';
import { SkeletonModule } from 'primeng/skeleton';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';
import { RolePermissionBridgeService } from '../../../core/services/role-permission-bridge.service';
import { SharedRoleService } from '../../../core/services/shared-role.service';

// Use the same interface from the service or create compatible one
interface RolePermission {
  role: string;
  dashboard_global: boolean;
  dashboard_client: boolean;
  dashboard_leader: boolean;
  dashboard_language: boolean;
  dashboard_promotion: boolean;
  dashboard_medical_visit: boolean;
  cards_view_read: boolean;
  cards_view_write: boolean;
  career_details: boolean;
  card_view_validate_role: boolean;
  own_page: boolean;
  client_read: boolean;
  client_write: boolean;
  project_read: boolean;
  project_write: boolean;
  staffing_read: boolean;
  staffing_write: boolean;
  promotion_read: boolean;
  promotion_identify_candidate: boolean;
  promotion_confirm_identification: boolean;
  promotion_schedule_interview: boolean;
  promotion_manage_candidate: boolean;
  promotion_commit: boolean;
  evaluation_read: boolean;
  evaluation_write: boolean;
  evluation_assign_reviewers: boolean;
  pool_read: boolean;
  pool_write: boolean;
  manager_read: boolean;
  manager_write: boolean;
  manager_people: boolean;
  bsa: boolean;
  production: boolean;
  people_read: boolean;
  people_write: boolean;
  director_read: boolean;
  director_write: boolean;
  notification_read: boolean;
  salaries_read: boolean;
  salaries_write: boolean;
  salary_validate: boolean;
  config_staffing: boolean;
  config_bsa: boolean;
  config_invoicing: boolean;
  config_performance: boolean;
  config_user_management: boolean;
  is_super_admin?: boolean;
  invoicing_read: boolean;
  invoicing_service: boolean;
  invoicing_admin: boolean;
  extra_oncall_export: boolean;
  bsa_org_read: boolean;
  bsa_org_write: boolean;
  audit_log_read: boolean;
  feedback_assigned_handler: boolean;
  feedback_assignment_manager: boolean;
  opportunity_read: boolean;
  opportunity_write: boolean;
  demand_read: boolean;
  demand_write: boolean;
  people_at_risk_read: boolean;
  people_at_risk_write: boolean;
  people_with_bad_attitude_read: boolean;
  people_with_bad_attitude_write: boolean;
  junior_promotion_notification: boolean;
  trial_period_ending_notification: boolean;
  contract_ending_notification: boolean;
  internship_request_read: boolean;
  internship_request_write: boolean;
  internship_people_interview_read: boolean;
  internship_people_interview_write: boolean;
  internship_technical_interview_read: boolean;
  internship_technical_interview_write: boolean;
  internship_documents_read: boolean;
  internship_documents_write: boolean;
  internship_credentials_read: boolean;
  internship_credentials_write: boolean;
  internship_feedback_read: boolean;
  internship_feedback_write: boolean;
  internship_lists_read: boolean;
  internship_lists_write: boolean;
}

@Component({
  selector: 'em-role-mgmt',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    CheckboxModule,
    ButtonModule,
    ToastModule,
    TooltipModule,
    TabViewModule,
    SkeletonModule,
    TranslateModule,
    EmPermissionDirective,
  ],
  providers: [MessageService],
  templateUrl: './role-mgmt.component.html',
  styleUrl: './role-mgmt.component.scss',
})
export class RoleMgmtComponent implements OnInit, OnChanges, OnDestroy {
  Permission = Permission;

  @Input() selectedRole: any = null;
  editingRole: string | null = null;
  isLoading = false;
  hasData: boolean = false;

  rolePermissions: any[] = [];
  filteredRolePermissions: any[] = [];
  originalRolePermissions: any[] = [];

  // Used by the skeleton loader template to render a representative placeholder grid
  readonly skeletonRows: number[] = Array.from({ length: 8 }, (_, i) => i);
  readonly skeletonColumns: number[] = Array.from({ length: 10 }, (_, i) => i);

  permissionGroups: any[] = [];
  permissionTabs: any[] = [];
  activeTabIndex = 0;
  private originalEditableRole: RolePermission | null = null;
  private selectionRange: { start: number; end: number } | null = null;

  /** Field names currently inside the drag selection, kept in step with selectionRange. */
  private selectedFields = new Set<string>();
  private selectionAnchorField: keyof RolePermission | null = null;
  private isPainting = false;
  private paintValue = false;
  private paintedFields = new Set<string>();
  private paintingRole: RolePermission | null = null;
  private paintRowElement: HTMLElement | null = null;
  private readonly permissionFieldOrder: string[] = [
    'dashboard_global',
    'dashboard_client',
    'dashboard_leader',
    'dashboard_language',
    'dashboard_promotion',
    'dashboard_medical_visit',
    'cards_view_read',
    'cards_view_write',
    'career_details_read',
    'card_view_validate_role',
    'own_page',
    'client_read',
    'client_write',
    'project_read',
    'project_write',
    'staffing_read',
    'staffing_write',
    'promotion_read',
    'promotion_identify_candidate',
    'promotion_confirm_identification',
    'promotion_commit',
    'promotion_schedule_interview',
    'promotion_manage_candidate',
    'evaluation_read',
    'evaluation_write',
    'pool_read',
    'pool_write',
    'opportunity_read',
    'opportunity_write',
    'demand_read',
    'demand_write',
    'people_at_risk_read',
    'people_at_risk_write',
    'people_with_bad_attitude_read',
    'people_with_bad_attitude_write',
    'manager_read',
    'manager_write',
    'manager_people',
    'bsa',
    'production',
    'people_read',
    'people_write',
    'payroll_read',
    'payroll_write',
    'notification_read',
    'salaries_read',
    'salaries_write',
    'salary_validate',
    'config_staffing',
    'config_bsa',
    'config_invoicing',
    'config_performance',
    'config_user_management',
    'invoicing_read',
    'invoicing_service',
    'invoicing_admin',
    'extra_oncall_export',
    'bsa_org_read',
    'bsa_org_write',
    'audit_log_read',
    'feedback_assigned_handler',
    'feedback_assignment_manager',

    'career_details_write',

    'overview_read',
    'overview_write',
    'basic_employee_info',
    'civil_state_details_read',
    'civil_state_details_write',
    'location_details_read',
    'location_details_write',
    'social_security_details_read',
    'social_security_details_write',
    'afma_read',
    'afma_write',
    'contract_details_read',
    'contract_details_write',
    'education_read',
    'education_write',
    'languages_read',
    'languages_write',
    'financial_details_read',
    'financial_details_write',
    'salary_advances_read',
    'salary_advances_write',
    'documents_read',
    'documents_write',
    'equipment_read',
    'equipment_write',
    'medical_visit_read',
    'medical_visit_write',
    'pay_slip_read',
    'pay_slip_write',
    'certifications_download',
    'skills_read',
    'skills_write',
    'director_read',
    'director_write',
  ];
  private static readonly NO_GROUPS: any[] = [];

  private cachedColumnsSource: any[] | null = null;
  private cachedColumns: any[] = [];
  private cachedAllColumnsSource: any[] | null = null;
  private cachedAllColumns: any[] = [];

  get permissionColumns(): any[] {
    const groups = this.activePermissionTab?.groups ?? RoleMgmtComponent.NO_GROUPS;
    if (groups !== this.cachedColumnsSource) {
      this.cachedColumnsSource = groups;
      this.cachedColumns = this.getColumnsForGroups(groups);
    }
    return this.cachedColumns;
  }

  get allPermissionColumns(): any[] {
    if (this.permissionGroups !== this.cachedAllColumnsSource) {
      this.cachedAllColumnsSource = this.permissionGroups;
      this.cachedAllColumns = this.getColumnsForGroups(this.permissionGroups);
    }
    return this.cachedAllColumns;
  }

  get activePermissionTab(): any | undefined {
    return this.permissionTabs[this.activeTabIndex];
  }

  getGroupColumnCount(group: any): number {
    if (group.sections) {
      return group.sections.reduce((total: number, section: any) => total + section.columns.length, 0);
    }
    return group.columns.length;
  }

  onPermissionTabChange(index: number): void {
    this.activeTabIndex = index;
    this.clearPermissionSelection();
    this.stopPermissionPaint();
  }

  private getColumnsForGroups(groups: any[]): any[] {
    return groups.flatMap((group) => {
      if (group.sections) {
        return group.sections.flatMap((section: any) => section.columns);
      }
      return group.columns;
    });
  }

  private createPlaceholderGroup(groupName: string, headers: string[]): any {
    return {
      groupName,
      columns: headers.map((header) => ({ header, disabled: true })),
    };
  }
    private createReadWriteGroup(groupName: string, fieldPrefix: string): any {
    return {
      groupName,
      columns: [
        { field: `${fieldPrefix}_read`, header: this.translate.instant('layout.configurations.permissions.read') },
        { field: `${fieldPrefix}_write`, header: this.translate.instant('layout.configurations.permissions.write') },
      ],
    };
  }

  private loadPermissionTabs(): void {
    const [
      dashboard,
      cardsView,
      client,
      project,
      staffing,
      promotion,
      evaluation,
      pool,
      manager,
      people,
      director,
      notification,
      invoicing,
      configuration,
      bsaOrg,
      teamAccess,
      auditLog,
      feedback,
      opportunities,
      demands,
      peopleAtRisk,
      peopleWithBadAttitude,
      internshipRequests,
      internshipPeopleInterview,
      internshipTechnicalInterview,
      internshipDocuments,
      internshipCredentials,
      internshipFeedback,
      internshipLists,
    ] = this.permissionGroups;

    const userManagement = this.createPlaceholderGroup(this.translate.instant('layout.configurations.permissions.userManagement'), [
      this.translate.instant('layout.configurations.permissions.role'),
      this.translate.instant('layout.configurations.permissions.permission'),
      this.translate.instant('layout.configurations.permissions.user'),
    ]);

    const exportImport = {
      groupName: this.translate.instant('layout.configurations.permissions.exportImport'),
      columns: [
        { field: 'export_import_import_employee', header: this.translate.instant('layout.configurations.permissions.importEmployee') },
        { field: 'export_import_import_employees', header: this.translate.instant('layout.configurations.permissions.importEmployees') },
        { field: 'export_import_missing_documents', header: this.translate.instant('layout.configurations.permissions.missingDocuments') },
        { field: 'export_import_seniority', header: this.translate.instant('layout.configurations.permissions.seniority') },
        { field: 'export_import_afma', header: 'AFMA' },
        { field: 'export_import_medical_visit', header: this.translate.instant('layout.configurations.permissions.medicalVisit') },
        { field: 'export_import_school_grant', header: this.translate.instant('layout.configurations.permissions.eligibleForSchoolGrant') },
        { field: 'export_import_staffing_bsa', header: this.translate.instant('layout.configurations.permissions.staffingBsa') },
        { field: 'export_import_staffing_production', header: this.translate.instant('layout.configurations.permissions.staffingProduction') },
        { field: 'export_import_pay_slip', header: this.translate.instant('layout.configurations.permissions.importNewPaySlip') },
        { field: 'export_import_salary', header: this.translate.instant('layout.configurations.permissions.importSalary') },
      ],
    };

    configuration.columns = [
      { field: 'config_bsa', header: this.translate.instant('layout.configurations.permissions.bsa') },
      { field: 'config_staffing', header: this.translate.instant('layout.configurations.permissions.staffing') },
      { field: 'config_invoicing', header: this.translate.instant('layout.configurations.permissions.invoicing') },
      { field: 'config_performance', header: this.translate.instant('layout.configurations.permissions.other') },
      { field: 'config_user_management', header: this.translate.instant('layout.configurations.permissions.userManagement') },
      { field: 'config_export_import', header: this.translate.instant('layout.configurations.permissions.exportImport') },
    ];

    this.permissionTabs = [
      { label: 'BSA', icon: 'pi pi-briefcase', groups: [cardsView, notification, bsaOrg] },
      { label: this.translate.instant('layout.configurations.permissions.staffing'), icon: 'pi pi-users', groups: [client, project, staffing, pool, promotion, evaluation] },
      {
        label: this.translate.instant('layout.configurations.permissions.internshipManagement'),
        icon: 'pi pi-id-card',
        groups: [internshipRequests, internshipPeopleInterview, internshipTechnicalInterview, internshipDocuments, internshipCredentials, internshipFeedback, internshipLists],
      },
      { label: this.translate.instant('layout.configurations.permissions.invoicing'), icon: 'pi pi-wallet', groups: [invoicing] },
      { label: this.translate.instant('layout.configurations.permissions.userManagement'), icon: 'pi pi-user-edit', groups: [userManagement] },
      { label: this.translate.instant('layout.configurations.permissions.configuration'), icon: 'pi pi-cog', groups: [configuration] },
      { label: this.translate.instant('layout.configurations.permissions.opportunities'), icon: 'pi pi-briefcase', groups: [opportunities, demands, peopleAtRisk, peopleWithBadAttitude] },
      { label: this.translate.instant('layout.configurations.permissions.exportImport'), icon: 'pi pi-sync', groups: [exportImport] },
      { label: this.translate.instant('layout.configurations.permissions.other'), icon: 'pi pi-ellipsis-h', groups: [dashboard, teamAccess, manager, people, director, auditLog, feedback] },
    ];
  }

  constructor(
    private messageService: MessageService,
    private rolePermissionBridge: RolePermissionBridgeService,
    private sharedRoleService: SharedRoleService,
    private translate: TranslateService,
    private zone: NgZone,
  ) { }

  ngOnInit(): void {
    this.loadRolePermissions();
    this.loadPermissionGroups();

    this.translate.onLangChange.subscribe(() => {
      this.loadPermissionGroups();
    });

    this.sharedRoleService.roles$.subscribe((roles) => {
      this.updateRolePermissionsFromSharedRoles(roles);
    });

    this.zone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.onDocumentMouseMove);
    });
  }

  loadPermissionGroups(): void {
    this.permissionGroups = [
      {
        groupName: this.translate.instant('layout.configurations.permissions.dashboard'),
        columns: [
          { field: 'dashboard_global', header: this.translate.instant('layout.configurations.permissions.global') },
          { field: 'dashboard_client', header: this.translate.instant('layout.configurations.permissions.client') },
          { field: 'dashboard_leader', header: this.translate.instant('layout.configurations.permissions.leader') },
          { field: 'dashboard_language', header: this.translate.instant('layout.configurations.permissions.language') },
          { field: 'dashboard_promotion', header: this.translate.instant('layout.configurations.permissions.promotion') },
          { field: 'dashboard_medical_visit', header: this.translate.instant('layout.configurations.permissions.medicalVisit') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.cardsView'),
        sections: [
          {
            sectionName: this.translate.instant('layout.configurations.permissions.general'),
            columns: [
              { field: 'cards_view_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'cards_view_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.overview'),
            columns: [
              { field: 'overview_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'overview_write', header: this.translate.instant('layout.configurations.permissions.write') },
              { field: 'basic_employee_info', header: this.translate.instant('layout.configurations.permissions.basicEmployeeInfo') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.civilStateDetails'),
            columns: [
              { field: 'civil_state_details_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'civil_state_details_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.locationDetails'),
            columns: [
              { field: 'location_details_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'location_details_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.socialSecurityDetails'),
            columns: [
              { field: 'social_security_details_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'social_security_details_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.afma'),
            columns: [
              { field: 'afma_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'afma_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.contractDetails'),
            columns: [
              { field: 'contract_details_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'contract_details_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.careerDetails'),
            columns: [
              { field: 'career_details_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'career_details_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.education'),
            columns: [
              { field: 'education_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'education_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.languages'),
            columns: [
              { field: 'languages_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'languages_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.financialDetails'),
            columns: [
              { field: 'financial_details_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'financial_details_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.salaries'),
            columns: [
              { field: 'salaries_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'salaries_write', header: this.translate.instant('layout.configurations.permissions.write') },
              { field: 'salary_validate', header: this.translate.instant('layout.configurations.permissions.validateSalary') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.salaryAdvances'),
            columns: [
              { field: 'salary_advances_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'salary_advances_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.documents'),
            columns: [
              { field: 'documents_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'documents_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.equipment'),
            columns: [
              { field: 'equipment_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'equipment_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.medicalVisit'),
            columns: [
              { field: 'medical_visit_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'medical_visit_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.cardPaySlip'),
            columns: [
              { field: 'pay_slip_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'pay_slip_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.certifications'),
            columns: [
              { field: 'certifications_download', header: this.translate.instant('layout.configurations.permissions.download') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.skills'),
            columns: [
              { field: 'skills_read', header: this.translate.instant('layout.configurations.permissions.read') },
              { field: 'skills_write', header: this.translate.instant('layout.configurations.permissions.write') },
            ],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.validateRole'),
            columns: [{ field: 'card_view_validate_role', header: '' }],
          },
          {
            sectionName: this.translate.instant('layout.configurations.permissions.ownPage'),
            columns: [{ field: 'own_page', header: '' }],
          },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.client'),
        columns: [
          { field: 'client_read', header: this.translate.instant('layout.configurations.permissions.read') },
          { field: 'client_write', header: this.translate.instant('layout.configurations.permissions.write') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.project'),
        columns: [
          { field: 'project_read', header: this.translate.instant('layout.configurations.permissions.read') },
          { field: 'project_write', header: this.translate.instant('layout.configurations.permissions.write') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.staffing'),
        columns: [
          { field: 'staffing_read', header: this.translate.instant('layout.configurations.permissions.read') },
          { field: 'staffing_write', header: this.translate.instant('layout.configurations.permissions.write') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.promotion'),
        columns: [
          { field: 'promotion_read', header: this.translate.instant('layout.configurations.permissions.read') },
          { field: 'promotion_identify_candidate', header: this.translate.instant('layout.configurations.permissions.identifyCandidate') },
          { field: 'promotion_confirm_identification', header: this.translate.instant('layout.configurations.permissions.confirmIdentification') },
          { field: 'promotion_commit', header: this.translate.instant('layout.configurations.permissions.committee') },
          { field: 'promotion_schedule_interview', header: this.translate.instant('layout.configurations.permissions.scheduleInterview') },
          { field: 'promotion_manage_candidate', header: this.translate.instant('layout.configurations.permissions.manageCandidate') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.evaluation'),
        columns: [
          { field: 'evaluation_read', header: this.translate.instant('layout.configurations.permissions.read') },
          { field: 'evaluation_write', header: this.translate.instant('layout.configurations.permissions.write') },
          { field: 'evluation_assign_reviewers', header: this.translate.instant('layout.configurations.permissions.assignReviewers') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.pool'),
        columns: [
          { field: 'pool_read', header: this.translate.instant('layout.configurations.permissions.read') },
          { field: 'pool_write', header: this.translate.instant('layout.configurations.permissions.write') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.manager'),
        columns: [
          { field: 'manager_read', header: this.translate.instant('layout.configurations.permissions.read') },
          { field: 'manager_write', header: this.translate.instant('layout.configurations.permissions.write') },
          { field: 'manager_people', header: this.translate.instant('layout.configurations.permissions.people') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.people'),
        columns: [
          { field: 'people_read', header: this.translate.instant('layout.configurations.permissions.read') },
          { field: 'people_write', header: this.translate.instant('layout.configurations.permissions.write') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.director'),
        columns: [
          { field: 'director_read', header: this.translate.instant('layout.configurations.permissions.read') },
          { field: 'director_write', header: this.translate.instant('layout.configurations.permissions.write') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.notification'),
        columns: [
          { field: 'junior_promotion_notification', header: this.translate.instant('layout.configurations.permissions.juniorPromotion') },
          { field: 'trial_period_ending_notification', header: this.translate.instant('layout.configurations.permissions.trialPeriodEnding') },
          { field: 'contract_ending_notification', header: this.translate.instant('layout.configurations.permissions.contractEnding') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.invoicing'),
        columns: [
          { field: 'invoicing_read', header: this.translate.instant('layout.configurations.permissions.invoicingRead') },
          { field: 'invoicing_service', header: this.translate.instant('layout.configurations.permissions.invoicingService') },
          { field: 'invoicing_admin', header: this.translate.instant('layout.configurations.permissions.invoicingAdmin') },
          { field: 'extra_oncall_export', header: this.translate.instant('layout.configurations.permissions.extraOnCallExport') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.configuration'),
        columns: [
          { field: 'config_staffing', header: this.translate.instant('layout.configurations.permissions.staffing') },
          { field: 'config_bsa', header: this.translate.instant('layout.configurations.permissions.bsa') },
          { field: 'config_invoicing', header: this.translate.instant('layout.configurations.permissions.invoicing') },
          { field: 'config_performance', header: this.translate.instant('layout.configurations.permissions.performance') },
          { field: 'config_user_management', header: this.translate.instant('layout.configurations.permissions.userManagement') },
        ],
      },
      {
        groupName: 'BSA ORG',
        columns: [
          { field: 'bsa_org_read', header: this.translate.instant('layout.configurations.permissions.read') },
          { field: 'bsa_org_write', header: this.translate.instant('layout.configurations.permissions.write') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.team'),
        columns: [
          { field: 'bsa', header: this.translate.instant('layout.configurations.permissions.bsa') },
          { field: 'production', header: this.translate.instant('layout.configurations.permissions.production') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.auditLog'),
        columns: [
          { field: 'audit_log_read', header: this.translate.instant('layout.configurations.permissions.read') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.feedback'),
        columns: [
          { field: 'feedback_assigned_handler', header: this.translate.instant('layout.configurations.permissions.assignedHandler') },
          { field: 'feedback_assignment_manager', header: this.translate.instant('layout.configurations.permissions.assignmentManager') },
        ],
      },
      {
        groupName: 'Opportunities',
        columns: [
          { field: 'opportunity_read', header: this.translate.instant('layout.configurations.permissions.read') },
          { field: 'opportunity_write', header: this.translate.instant('layout.configurations.permissions.write') },
        ],
      },
      {
        groupName: 'Demandes',
        columns: [
          { field: 'demand_read', header: this.translate.instant('layout.configurations.permissions.read') },
          { field: 'demand_write', header: this.translate.instant('layout.configurations.permissions.write') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.peopleAtRisk'),
        columns: [
          { field: 'people_at_risk_read', header: this.translate.instant('layout.configurations.permissions.read') },
          { field: 'people_at_risk_write', header: this.translate.instant('layout.configurations.permissions.write') },
        ],
      },
      {
        groupName: this.translate.instant('layout.configurations.permissions.peopleWithBadAttitude'),
        columns: [
          { field: 'people_with_bad_attitude_read', header: this.translate.instant('layout.configurations.permissions.read') },
          { field: 'people_with_bad_attitude_write', header: this.translate.instant('layout.configurations.permissions.write') },
        ],
      },
          this.createReadWriteGroup(this.translate.instant('layout.configurations.permissions.requests'), 'internship_request'),
          this.createReadWriteGroup(this.translate.instant('layout.configurations.permissions.peopleInterview'), 'internship_people_interview'),
          this.createReadWriteGroup(this.translate.instant('layout.configurations.permissions.technicalInterview'), 'internship_technical_interview'),
          this.createReadWriteGroup(this.translate.instant('layout.configurations.permissions.documents'), 'internship_documents'),
          this.createReadWriteGroup(this.translate.instant('layout.configurations.permissions.credentials'), 'internship_credentials'),
          this.createReadWriteGroup(this.translate.instant('layout.configurations.permissions.feedback'), 'internship_feedback'),
          this.createReadWriteGroup(this.translate.instant('layout.configurations.permissions.listsArchive'), 'internship_lists'),
    ];

    this.loadPermissionTabs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedRole'] && this.rolePermissions.length > 0) {
      this.filterRolePermissions();
    }
  }

  private filterRolePermissions(): void {
    if (this.selectedRole && this.selectedRole.name) {
      const shortName = this.getShortRoleName(this.selectedRole.name);
      this.filteredRolePermissions = this.rolePermissions.filter((role) => role.role === shortName);
    } else {
      this.filteredRolePermissions = [...this.rolePermissions].sort((a, b) => {
        if (a.is_super_admin) return -1;
        if (b.is_super_admin) return 1;
        return 0;
      });
    }
  }

  convertToBinaryString(rolePermission: any): string {
    const permissions = [
      rolePermission.dashboard_global || rolePermission.dashboard_client || rolePermission.dashboard_leader || rolePermission.dashboard_language || rolePermission.dashboard_promotion || rolePermission.dashboard_medical_visit,
      rolePermission.cards_view_read, rolePermission.cards_view_write, rolePermission.career_details_read,
      rolePermission.card_view_validate_role, rolePermission.own_page, rolePermission.client_read, rolePermission.client_write,
      rolePermission.project_read, rolePermission.project_write, rolePermission.staffing_read, rolePermission.staffing_write,
      rolePermission.promotion_read, rolePermission.promotion_identify_candidate, rolePermission.promotion_confirm_identification,
      rolePermission.promotion_schedule_interview, rolePermission.promotion_manage_candidate, rolePermission.promotion_commit,
      rolePermission.evaluation_read, rolePermission.evaluation_write, rolePermission.pool_read, rolePermission.pool_write,
      rolePermission.manager_read, rolePermission.manager_write, rolePermission.manager_people,
      rolePermission.people_read, rolePermission.people_write, rolePermission.notification_read,
      rolePermission.salaries_read, rolePermission.salaries_write, rolePermission.salary_validate,
      rolePermission.config_staffing, rolePermission.config_bsa, rolePermission.config_invoicing,
      rolePermission.config_performance, rolePermission.config_user_management,
      rolePermission.invoicing_read, rolePermission.invoicing_service, rolePermission.invoicing_admin,
      rolePermission.extra_oncall_export, rolePermission.bsa_org_read, rolePermission.bsa_org_write,
      rolePermission.audit_log_read, rolePermission.feedback_assigned_handler, rolePermission.feedback_assignment_manager,
      rolePermission.career_details_write, rolePermission.overview_read, rolePermission.overview_write,
      rolePermission.basic_employee_info, rolePermission.civil_state_details_read, rolePermission.civil_state_details_write,
      rolePermission.location_details_read, rolePermission.location_details_write,
      rolePermission.social_security_details_read, rolePermission.social_security_details_write,
      rolePermission.afma_read, rolePermission.afma_write, rolePermission.contract_details_read, rolePermission.contract_details_write,
      rolePermission.education_read, rolePermission.education_write, rolePermission.languages_read, rolePermission.languages_write,
      rolePermission.financial_details_read, rolePermission.financial_details_write,
      rolePermission.salary_advances_read, rolePermission.salary_advances_write,
      rolePermission.documents_read, rolePermission.documents_write, rolePermission.equipment_read, rolePermission.equipment_write,
      rolePermission.medical_visit_read, rolePermission.medical_visit_write,
      rolePermission.pay_slip_read, rolePermission.pay_slip_write, rolePermission.certifications_download,
      rolePermission.skills_read, rolePermission.skills_write,
      rolePermission.junior_promotion_notification, rolePermission.trial_period_ending_notification, rolePermission.contract_ending_notification,
      rolePermission.director_read, rolePermission.director_write,
    ];
    return permissions.map((p) => (p ? '1' : '0')).join('');
  }

  getShortRoleName(longName: string): string {
    return this.sharedRoleService.getShortRoleName(longName);
  }

  loadRolePermissions(): void {
    this.isLoading = true;

    this.rolePermissionBridge.loadRolePermissionsFromApi().subscribe({
      next: (rolePermissions) => {
        this.rolePermissions = rolePermissions;
        this.hasData = rolePermissions.length > 0;
        this.filterRolePermissions();
        this.isLoading = false;

        if (!this.hasData) {
          this.messageService.add({
            severity: 'info',
            summary: this.translate.instant('layout.configurations.permissions.noData.title'),
            detail: this.translate.instant('layout.configurations.permissions.noData.detail'),
          });
        }
      },
      error: (error) => {
        console.error('Error loading role permissions:', error);
        this.rolePermissions = [];
        this.hasData = false;
        this.filterRolePermissions();
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('layout.configurations.permissions.loadError.title'),
          detail: this.translate.instant('layout.configurations.permissions.loadError.detail'),
        });
      },
    });
  }

  private updateRolePermissionsFromSharedRoles(roles: any[]): void {
    roles.forEach((role) => {
      if (!this.sharedRoleService.hasRoleMapping(role.name)) {
        this.sharedRoleService.addRoleMapping(role.name, this.generateShortName(role.name));
      }
    });
  }

  private generateShortName(longName: string): string {
    const words = longName.split(' ');
    let shortName = words.map((word) => word.charAt(0).toUpperCase()).join('');
    if (shortName.length > 4) shortName = shortName.substring(0, 4);
    let counter = 1;
    let finalShortName = shortName;
    while (this.sharedRoleService.hasRoleMapping(finalShortName)) {
      finalShortName = shortName + counter;
      counter++;
    }
    return finalShortName;
  }

  private getLongRoleName(shortName: string): string {
    return this.sharedRoleService.getLongRoleName(shortName);
  }

  onPermissionChange(role: RolePermission, field: keyof RolePermission, value: boolean): void {
    if (!this.isRoleEditable(role.role)) return;
    const canonicalRole = this.findRole(role.role);
    if (!canonicalRole) return;

    (canonicalRole as any)[field] = value;
    if (canonicalRole !== role) (role as any)[field] = value;

    if (value && typeof field === 'string' && field.endsWith('_write')) {
      const readField = field.replace('_write', '_read');
      if (readField in canonicalRole) (canonicalRole as any)[readField] = true;
    }

    // Pool depends on Staffing: POOL Read requires STAFFING Read.
    if (field === 'pool_read' && value) {
     canonicalRole.staffing_read = true;
    } else if (field === 'staffing_read' && !value) {
     canonicalRole.pool_read = false;
    }

    if (field === 'invoicing_service') {
      if (value) canonicalRole.invoicing_read = true;
      else canonicalRole.invoicing_admin = false;
    } else if (field === 'invoicing_admin' && value) {
      canonicalRole.invoicing_read = true;
      canonicalRole.invoicing_service = true;
    } else if (field === 'invoicing_read' && !value) {
      canonicalRole.invoicing_service = false;
      canonicalRole.invoicing_admin = false;
    }
  }

  getPermissionDisabled(role: RolePermission): boolean {
    return !this.isRoleEditable(role.role);
  }

  enableModification(roleName: string): void {
    const canonicalRole = this.findRole(roleName);
    if (!canonicalRole || this.editingRole) return;
    this.editingRole = canonicalRole.role;
    this.originalEditableRole = JSON.parse(JSON.stringify(canonicalRole));
    this.clearPermissionSelection();
  }

  cancelEdit(): void {
    const editableRole = this.findRole(this.editingRole);
    if (editableRole && this.originalEditableRole) {
      Object.assign(editableRole, this.originalEditableRole);
      this.filterRolePermissions();
    }
    this.originalEditableRole = null;
    this.editingRole = null;
    this.clearPermissionSelection();
    this.stopPermissionPaint();
    this.messageService.add({
      severity: 'info',
      summary: this.translate.instant('layout.configurations.permissions.CancelEdit.title'),
      detail: this.translate.instant('layout.configurations.permissions.CancelEdit.detail'),
    });
  }

  isRoleEditable(roleName: string): boolean {
    return this.editingRole === roleName;
  }

  trackByColumn = (_index: number, column: { field?: string; header?: string }): string =>
    column.field ?? column.header ?? String(_index);

  trackByRole = (_index: number, role: RolePermission): string => role.role;

  saveChanges(): void {
    if (!this.editingRole) return;
    this.isLoading = true;
    const rolePermissionData = this.rolePermissions.find((rp) => rp.role === this.editingRole);
    if (rolePermissionData) {
      this.rolePermissionBridge.saveRolePermissionsToApi(rolePermissionData).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('layout.configurations.permissions.updatePermissions.title'),
            detail: this.translate.instant('layout.configurations.permissions.updatePermissions.detail', { role: this.editingRole }),
          });
          this.editingRole = null;
          this.originalEditableRole = null;
          this.clearPermissionSelection();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error saving role permissions:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('layout.configurations.permissions.saveError.title'),
            detail: this.translate.instant('layout.configurations.permissions.saveError.detail', { role: this.editingRole }),
          });
          this.isLoading = false;
        },
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.toast.error'),
        detail: this.translate.instant('layout.configurations.permissions.roleDataNotFound', { role: this.editingRole }),
      });
      this.isLoading = false;
    }
  }

  setAllPermissions(role: RolePermission, value: boolean): void {
    this.allPermissionColumns
      .filter((column: { disabled?: boolean }) => !column.disabled)
      .forEach((column: { field: keyof RolePermission }) => this.onPermissionChange(role, column.field, value));
  }

  getRowCheckState(role: RolePermission): 'checked' | 'partial' | 'unchecked' {
    const values = this.allPermissionColumns
      .filter((column: { disabled?: boolean }) => !column.disabled)
      .map((column: { field: keyof RolePermission }) => Boolean(role[column.field]));
    return values.every(Boolean) ? 'checked' : values.some(Boolean) ? 'partial' : 'unchecked';
  }

  toggleRowMasterCheckbox(role: RolePermission): void {
    this.setAllPermissions(role, this.getRowCheckState(role) !== 'checked');
  }

  getRowMasterIcon(role: RolePermission): string {
    switch (this.getRowCheckState(role)) {
      case 'checked': return 'pi pi-check-square';
      case 'partial': return 'pi pi-minus';
      default: return 'pi pi-times';
    }
  }

  onPermissionKeydown(event: KeyboardEvent, role: RolePermission, field: keyof RolePermission): void {
    if (this.getPermissionDisabled(role)) return;
    const fields = this.permissionColumns.map((column: { field: keyof RolePermission }) => column.field);
    const currentIndex = fields.indexOf(field);
    let targetIndex = currentIndex;

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.togglePermissionSelection(role, field);
      return;
    }

    if (event.key === 'ArrowLeft') targetIndex--;
    if (event.key === 'ArrowRight') targetIndex++;
    if (event.key === 'Home') targetIndex = 0;
    if (event.key === 'End') targetIndex = fields.length - 1;

    if (targetIndex !== currentIndex) {
      event.preventDefault();
      targetIndex = Math.max(0, Math.min(fields.length - 1, targetIndex));
      if (event.shiftKey) {
        this.selectionAnchorField ??= field;
        this.extendSelectionRange(this.selectionAnchorField, fields[targetIndex]);
      } else {
        this.selectionAnchorField = fields[targetIndex];
        this.selectionRange = null;
        this.selectedFields.clear();
      }
      this.focusPermissionCell(role.role, fields[targetIndex]);
    }
  }

  isFieldInSelectionRange(field: keyof RolePermission): boolean {
    return this.selectedFields.has(String(field));
  }

  beginPermissionPaint(event: MouseEvent, role: RolePermission, field: keyof RolePermission, rowElement: HTMLElement): void {
    if (event.button !== 0 || this.getPermissionDisabled(role)) return;
    event.preventDefault();
    this.selectionAnchorField = field;
    this.extendSelectionRange(field, field);
    this.isPainting = true;
    this.paintValue = !Boolean(role[field]);
    this.paintedFields = new Set<string>();
    this.paintingRole = role;
    this.paintRowElement = rowElement;
    this.paintSelectionRange(role);
    this.focusPermissionCell(role.role, field);
  }

  private readonly onDocumentMouseMove = (event: MouseEvent): void => {
    if (!this.isPainting || !this.paintingRole || !this.selectionAnchorField) return;
    const field = this.getPermissionFieldAtHorizontalPosition(event);
    if (!field) return;
    event.preventDefault();
    this.zone.run(() => {
      this.extendSelectionRange(this.selectionAnchorField!, field);
      this.paintSelectionRange(this.paintingRole!);
      this.focusPermissionCell(this.paintingRole!.role, field);
    });
  };

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    this.stopPermissionPaint();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.permission-cell')) this.clearPermissionSelection();
  }

  paintPermission(role: RolePermission, field: keyof RolePermission): void {
    if (!this.isPainting || this.paintedFields.has(String(field))) return;
    this.paintedFields.add(String(field));
    this.onPermissionChange(role, field, this.paintValue);
  }

  stopPermissionPaint(): void {
    this.isPainting = false;
    this.paintingRole = null;
    this.paintRowElement = null;
    this.paintedFields.clear();
  }

  private paintSelectionRange(role: RolePermission): void {
    this.getSelectedPermissionFields(this.selectionAnchorField as keyof RolePermission).forEach((field) =>
      this.paintPermission(role, field),
    );
  }

  private getPermissionFieldAtHorizontalPosition(event: MouseEvent): keyof RolePermission | null {
    const pointedElement = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement | null;
    const pointedCell = pointedElement?.closest<HTMLElement>('.permission-cell[data-permission-field]');

    if (pointedCell?.dataset['permissionField'] && this.paintRowElement?.contains(pointedCell)) {
      return pointedCell.dataset['permissionField'] as keyof RolePermission;
    }

    const permissionCells = Array.from(
      this.paintRowElement?.querySelectorAll<HTMLElement>('.permission-cell[data-permission-field]') ?? [],
    );

    return (
      (permissionCells.find((cell) => {
        const { left, right } = cell.getBoundingClientRect();
        return event.clientX >= left && event.clientX <= right;
      })?.dataset['permissionField'] as keyof RolePermission | undefined) ?? null
    );
  }

  onPermissionCellMouseEnter(role: RolePermission, field: keyof RolePermission): void {
    if (this.isPainting && role === this.paintingRole) {
      this.extendSelectionRange(this.selectionAnchorField as keyof RolePermission, field);
      this.paintSelectionRange(role);
      this.focusPermissionCell(role.role, field);
    }
  }

  private togglePermissionSelection(role: RolePermission, focusedField: keyof RolePermission): void {
    const selectedFields = this.getSelectedPermissionFields(focusedField);
    const nextValue = !Boolean(role[focusedField]);
    selectedFields.forEach((field) => this.onPermissionChange(role, field, nextValue));
  }

  private getSelectedPermissionFields(fallbackField: keyof RolePermission): (keyof RolePermission)[] {
    if (!this.selectionRange) return [fallbackField];
    return this.permissionColumns
      .map((column: { field: keyof RolePermission }) => column.field)
      .slice(this.selectionRange.start, this.selectionRange.end + 1);
  }

  private extendSelectionRange(anchorField: keyof RolePermission, targetField: keyof RolePermission): void {
    const fields = this.permissionColumns.map((column: { field: keyof RolePermission }) => column.field);
    const start = fields.indexOf(anchorField);
    const end = fields.indexOf(targetField);

    if (start === -1 || end === -1) {
      this.clearPermissionSelection();
      return;
    }

    this.selectionRange = { start: Math.min(start, end), end: Math.max(start, end) };
    this.selectedFields = new Set(
      fields.slice(this.selectionRange.start, this.selectionRange.end + 1).map(String)
    );
  }

  private clearPermissionSelection(): void {
    this.selectionAnchorField = null;
    this.selectionRange = null;
    this.selectedFields.clear();
  }

  private focusPermissionCell(roleName: string, field: keyof RolePermission): void {
    const escapedRole = roleName.trim().toLocaleLowerCase().replace(/"/g, '\\"');
    const escapedField = String(field).replace(/"/g, '\\"');
    const selector = `[data-role-key="${escapedRole}"][data-permission-field="${escapedField}"]`;
    (document.querySelector(selector) as HTMLElement | null)?.focus();
  }

  private findRole(roleName: string | null): RolePermission | undefined {
    if (!roleName) return undefined;
    return this.rolePermissions.find((role) => role.role === roleName);
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousemove', this.onDocumentMouseMove);
    this.clearPermissionSelection();
    this.stopPermissionPaint();
  }
}
